/* tslint:disable:import-spacing no-inferrable-types variable-name align no-trailing-whitespace */

import {
  DecisionPoint,
  SwitchingLayout}       from '@/models/scenario/decision.point';
import { Track }         from '@/models/scenario/track';
import {
  InterLink,
  InterLinkType}         from '@/models/scenario/inter.link';
import {Scenario}        from '@/models/scenario/scenario';
import {Logger}          from '@/util/logger';

import {ViewMemento}     from './view.memento';

import {ViewerInterface} from './viewer.interface';
import {AxisInterface}   from './axis.interface';

import * as $ from 'jquery';
import {Vector2} from 'three';

/**
 *
 * @class DistanceAxis
 *
 * @brief teh Y-axis for the main viewer
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      November 2019
 *
 */
export class DistanceAxis implements AxisInterface {

  /** @var _nightMode boolean are we in dark mode display? */
  private _nightMode: boolean          = true;

  /** @var _scenario Scenario the current data model */
  private _scenario: Scenario          = null;

  /** @var _viewer ViewerInterface the main viewer */
  private   _viewer: ViewerInterface   = null;

  /** @var canvas HTMLCanvasElement the canvas we draw on */
  private   _canvas: HTMLCanvasElement = null;

  /** @var _width number the current width of the widget */
  private    _width: number            = 0;

  /** @var _height number the current height of the widget */
  private   _height: number            = 0;

  /** @var _showStation are we showing the decision point names? */
  private _showStation: boolean        = true;

  private            _prefs: any               = {

    axis: {

      night: {
        fontSize: 12,
        tickColor: '#ffeb78',
        fontColor: '#ffffff'
      },

      day: {
        fontSize: 12,
        tickColor: '#5f7589',
        fontColor: '#000000'
      }
    }

  };

  /** @brief provide the DOM element ID of the canvas element */
  constructor(id: string, view: ViewerInterface) {

    this._canvas = document.getElementById(id) as HTMLCanvasElement;
    this._width  = $('#' + id).width();
    this._height = $('#' + id).height();
    this._viewer = view;

    this._canvas.width  = this._width;
    this._canvas.height = this._height;

    this.clear();

    /*
     * the distance axis has an expand/collapse button placed in its corner, to allow users
     * to de-clutter their display, add the click-to-toggle behavior...
     *
     */

    $('.distance-expand').on('click', (e) => {

      e.preventDefault();

      if (this._showStation) {

        this._showStation = false;

      } else {

        this._showStation = true;
      }

      /* refresh the display */

      this.update();

    });

    /* setup for axis scaling ... */

    $(this._canvas).off('wheel');
    $(this._canvas).on('wheel', (event) => {

      event.preventDefault();

      let vm: ViewMemento = this._viewer.viewMemento();

      if (vm == null) {

        /* nothing to do */

        return ;
      }

      /* before we scale try to find what y (distance) position is under the cursor */

      // @ts-ignore
      const sd: number  = event.originalEvent.offsetY;

      let y0            = Math.min(vm._screenGraph.max.y, vm._screenGraph.min.y);
      let y1            = Math.max(vm._screenGraph.max.y, vm._screenGraph.min.y);

      y0 = y0 < 0            ? 0           : y0;
      y1 = y1 > this._height ? this._height : y1;

      if ((y0  === Number.NEGATIVE_INFINITY) ||
          (y0  === Number.POSITIVE_INFINITY) ||
          isNaN(y0) ||
          (y1  === Number.NEGATIVE_INFINITY) ||
          (y1  === Number.POSITIVE_INFINITY) ||
          isNaN(y1) ||
          (y0 == y1)) {

        /* no bounds yet */

        return ;
      }

      /* if both are offscreen, we don't have to draw anything */

      if (((y0 < 0) || (y0 > this._height)) && ((y1 < 0) || (y1 > this._height))) {

        /* nothing to draw */

        return ;
      }

      if ((sd < y0 ) || (sd > y1)) {

        /* not currently over the axis */

        return ;
      }

      /* wd is the current time value (hours) under the cursor, where we want to come back to */

      const sp: number    = sd;
      let wd = this._viewer.screenToWorld(0, sp)[1];

      wd /= vm._distanceScale; /* adjust for existing axis scaling */

      /*
       * @note this is going to be called on each 'tick' of the wheel, so if you spin 4 ticks,
       * we will be re-scaling at each one, not all at once when you lift your finger up.
       *
       */

      // @ts-ignore
      const delta: number      = event.originalEvent.deltaY;
      let   scale: number      = Math.floor(vm._distanceScale);

      if (delta > 0) {

        /* scale down */

        scale--;

        this._viewer.scaleDistanceTo(scale);

      } else {

        /* scale up */

        scale++;

        this._viewer.scaleDistanceTo(scale);
      }

      /* now try to move the camera so 'wd' at the new scale, is back under the cursor */

      vm                   = this._viewer.viewMemento();

      const  cd: number    = wd * vm._distanceScale;
      const sp2: number    = this._viewer.worldToScreen(0, cd)[1];

      const sdd: number    = sp2 - sp;
      const  d1: number    = this._viewer.screenToWorld(0, 0)[1];
      const  d2: number    = this._viewer.screenToWorld(0, sdd)[1];

      /*
       * dDelta is the amount in the world space we have to adjust the camera by "bring it back"
       * to where we were.
       *
       */

      const dDelta: number = d2 - d1;

      this._viewer.panY(dDelta);
    });
  }

  /** @brief push the graph over so that world 0 is projected just after the edge of the axis */
  autoPan() {

    const vm: ViewMemento = this._viewer.viewMemento();

    if (vm == null) {

      /* nothing to do */

      return ;
    }

    const     camX: number = vm._camera.x;
    const   anchor: number = (this._width + 10);

    const sx               = this._viewer.worldToScreen(vm._graph.min.x, 0)[0];

    const sdelta           = (sx - anchor);

    const dx1              = this._viewer.screenToWorld(0,   0)[0];
    const dx2              = this._viewer.screenToWorld(sdelta, 0)[0];

    const delta            = (dx2 - dx1);

    Logger.debug('[distance] auto-panning (veritcal) cam=', camX, ' x=', vm._graph.min.x, ' delta=', delta);

    this._viewer.panY(delta);
  }

  /**
   * DEPRECATED - the panzoom controller is used now in ThreeStringViewer
   *
   * set the scale to make the graph just big enough, that it would fit exactly
   * in the current viewport.
   *
   */
  autoFit() {

    let vm: ViewMemento = this._viewer.viewMemento();

    if (vm == null) {

      /* nothing to do */

      Logger.warning(`[distance] autoFit - no memento.`);

      return ;
    }

    /* center of axis as anchor point */

    const sp: number = (this._height / 2) - 60;
    let   wd: number = this._viewer.screenToWorld(0, sp)[1];

    wd /= vm._distanceScale;

    /* fetch current height of graph in world space */

    const curHeight: number = vm._graph.getSize(new Vector2()).height;

    /* un-project the current height of the portal, that gives us the height of the graph */

    const y1                = this._viewer.screenToWorld(0, 0)[1];
    const y2                = this._viewer.screenToWorld(0, this._height - 30)[1];

    const tgtHeight: number = Math.abs(y2 - y1);

    /* scale fact is what gets us from current world height to the target world height. */

    if (curHeight >= tgtHeight) {

      /* it's already more than the portal can handle */

      return ;
    }

    const scale: number =  tgtHeight / curHeight;

    Logger.debug('[distance] auto-fitting world=', curHeight, ' max=', tgtHeight, ' scale > ', scale);

    this._viewer.scaleDistanceTo(scale);

    /* now try to drag the graph back so the anchor is at 0 again */

    vm                = this._viewer.viewMemento();

    const  cd: number = wd * vm._distanceScale;
    const sp2: number = this._viewer.worldToScreen(0, cd)[1];

    const sdd: number = sp2 - sp;
    const  d1: number = this._viewer.screenToWorld(0, 0)[1];
    const  d2: number = this._viewer.screenToWorld(0, sdd)[1];

    /*
     * dDelta is the amount in the world space we have to adjust the camera by "bring it back"
     * to where we were.
     *
     */

    const dDelta: number = d2 - d1;

    this._viewer.panY(dDelta / 2);
  }

  /** @brief clear the canvas */
  clear(): void {

    this._canvas.getContext('2d').clearRect(0, 0, this._width, this._height);
  }

  /** @brief fetch the screen width of this axis */
  width(): number {

    return this._width;
  }

  /** @brief fetch the screen height of this axis */
  height(): number {

    return this._height;
  }

  /** @brief start with a new scenario */
  setScenario(scenario: Scenario): void {

    this._scenario = scenario;

    /* initialize the axis ... */

    this.update();
  }

  /** @brief helper to generate the switch layout summary string for a decision point */
  public switchLayout(dp: DecisionPoint, il: InterLink, t: Track): string {

    /* the junction layout */

    let sStr: string = '';

    switch (dp.switchLayout(t)) {

      case SwitchingLayout.AllCross: {

        sStr = 'X' + '\u2798' + '\u279a';

        break;
      }

      case SwitchingLayout.NoCross: {

        if (il == null) {

          sStr = 'X' + '\u2212';

        } else {

          if (il._type === InterLinkType.DOUBLE) {

            sStr = 'X' + '\u21c6';

          } else {

            sStr = 'X' + '\u2212';

          }
        }
        break;
      }

      case SwitchingLayout.StraightThrough: {

        if (il == null) {

          sStr = 'X' + '\u2212';

        } else {

          if (il._type === InterLinkType.DOUBLE) {

            sStr = 'X' + '\u21c6';

          } else {

            sStr = 'X' + '\u2212';

          }
        }
        break;
      }

      case SwitchingLayout.LeftHandCross: {

        sStr = 'X' + '\u2798';

        break;
      }

      case SwitchingLayout.RightHandCross: {

        sStr = 'X' + '\u279a';

        break;
      }
    }

    return sStr;
  }

  /** @brief main view has changed in some way, update axis */
  update(): void {

    this.clear();

    const vm: ViewMemento = this._viewer.viewMemento();

    if (vm == null) {

      /* nothing to do */

      Logger.warning(`[distance] update - no memento.`);

      return ;
    }

    /*
     * first figure out what rectangle on the canvas we are painting within; the width is constant,
     * but if the projection of the main graph is either shorter or taller than the canvas, then we
     * have to adjust our clipping bound (vertically).  Also, canvas Y co-ords are inverted, so y0
     * is the top and y1 is the bottom; we show (0,0) at the bottom, hence, y1 (the min) is either
     * the projected height fo the graph or the height of the canvas whichever one is less. You can't
     * draw more than the height of canvas!  Its similar for y0, but we can't draw less than 0 for
     * the max.
     *
     */

    const y0: number      = vm._screenGraph.max.y < 0            ? 0            : vm._screenGraph.max.y;
    const y1: number      = vm._screenGraph.min.y > this._height ? this._height : vm._screenGraph.min.y;

    if ((y0  === Number.NEGATIVE_INFINITY) ||
        (y0  === Number.POSITIVE_INFINITY) ||
        isNaN(y0) ||
        (y1  === Number.NEGATIVE_INFINITY) ||
        (y1  === Number.POSITIVE_INFINITY) ||
        isNaN(y1) ||
        (y0 == y1)) {

      /* no bounds yet */

      Logger.warning(`[distance] update - no bounds yet.`);

      return ;
    }

    /* if both are off-screen, we don't have to draw anything */

    if (((y0 < 0) || (y0 > this._height)) && ((y1 < 0) || (y1 > this._height))) {

      /* nothing to draw */

      Logger.debug(`[distance] update - off screen.`);

      return ;
    }

    /* we need to draw whatever is between the project of y0 - y1 */

    const context = this._canvas.getContext('2d');

    /* debug */

    if (true) {

      context.strokeStyle = '#ffffff';
      context.lineWidth = 2;

      context.beginPath();
      context.moveTo(0, y0);
      context.lineTo(this._width, y0);
      context.stroke();

      context.beginPath();
      context.moveTo(0, y1);
      context.lineTo(this._width, y1);
      context.stroke();
    }

    /*
     * get the start/end values in world space; we un-project the min/max of what can
     * paint so that we know the vertical range (in the actual graph) that could even
     * be rendered, that lets us pick out only the decision points we have to render,
     * instead of trying to render them all (which may actually be a lot more)
     *
     * r0y and r1y are the un-projected versions of y0 and y1.
     *
     */

    const r0y  = this._viewer.screenToWorld(0, y0)[1];
    const r1y  = this._viewer.screenToWorld(0, y1)[1];

    /*
     * set the scaling factor for drawing; once we know the projected and projected
     * vertical bound, we can just use linear interpolation to draw the axis.  Keep in
     * mind though, the graph might be zoomed in either the vertical or horizontal
     * direction, or both, and it may even be panned (translated) to some position.
     *
     * For now, we just need to be mindful of the vertical zoom (distanceScale). The
     * un-projected y co-ords, must also be scaled to whatever the zoom level is.
     *
     * By the end of our search loop, bi and ei are the begin and end indexes (inclusive)
     * of which decision points we have to render.
     *
     */

    const scale = Math.abs((y1 - y0) / (r0y - r1y));

    // Logger.debug('[distance]', 'updating, begin: ', r0y, ' end:', r1y, ' scale:', scale);

    /* find the decision points in this range, these are the ones we are drawing at the major ticks */

    const dps: DecisionPoint[] = this._scenario.milePoints(true);
    let    bi: number          = 0;
    let    ei: number          = 0;

    for (let i = 0; i < dps.length; i++) {

      if (dps[i]._mileage <= r0y / vm._distanceScale) {

        bi = i - 4;
        break;
      }
    }

    if (bi < 0) {

      bi = 0;
    }

    for (let i = dps.length - 1; i > 0; i--) {

      if (Math.abs(dps[i]._mileage - r1y / vm._distanceScale) < 0.00001) {

        ei = i + 4;
        break;
      }

      if (dps[i]._mileage >= r1y / vm._distanceScale) {

        ei = i + 4;
        break;
      }
    }

    if (ei >= dps.length) {
      ei = dps.length - 1;
    }

    /*
     * Pre-scan to figure out how wide the canvas will need to be.  We have a fixed
     * width for the Y-axis, but we are right justifying, so we have to known how big
     * the biggest label is, so we can shift everything to that right bound.
     *
     * We use the max label/value width + a constant 40px to give us a reasonably wide
     * canvas, that has some padding around the labels.
     *
     * Note that we also have left justification; the values (the actual mileage number)
     * is shown to the right of the axis.  The overally layout for a given station (a
     * decision point) is like this:
     *
     *    <label> | <mileage>
     *
     * The '|' is the axis line that we draw the ticks against.
     *
     */

    const labels: Map<number, {text: string, value: string, y: number, lw: number, vw: number, height: number}> =
      new Map<number, {text: string, value: string, y: number, lw: number, vw: number, height: number}>();
    let    maxW: number = 0;
    let   maxLW: number = 0;
    let   maxVW: number = 0;
    const    fs: number = 12;
    const  left: number = 8;
    const  yOff: number = this._viewer.worldToScreen(0, vm._graph.min.y)[1] - this._viewer.worldToScreen(0,0)[1];
    let    minY: number = y0;

    context.font          = 'normal bold ' + fs + 'px Verdana';
    context.textBaseline  = 'middle';

    for (let i = ei; i >= bi; i--) {

      const       item: DecisionPoint = dps[i];

      const       sStr: string = this.switchLayout(item, null, null);
      const      value: number = item._mileage;
      const eastBundle: string = (item._eastSide == null) ? '' : ' [' + InterLink.typeToString( item._eastSide._type) + ']';
      let        label: string = (item._name.trim() + eastBundle + ' ' + sStr).trim();
      const        val: string = value.toFixed(1);
      let           lw: number = context.measureText(label).width;
      const         vw: number = context.measureText(val).width;
      const          h: number = fs;


      /* are we actually showing station names? */

      if (!this._showStation) {

        lw    = 0;
        label = '';
      }

      /*
       * the graph may not actually start at (0,0), we skip the blank space by starting our plotting
       * wherever the first point is (yOff below)
       *
       */

      const          y: number = (vm._screenGraph.min.y - yOff - item._mileage * scale * vm._distanceScale);

      if (y <= minY) {

        minY = y;
      }

      if (lw > maxLW) {

        maxLW = lw;
      }

      if (vw > maxVW) {

        maxVW = vw;
      }

      if ((maxLW + maxVW) > maxW) {

        maxW = maxLW + maxVW;
      }

      labels.set(i, {text: label, value: val, y, lw, vw, height: h});
    }

    /*
     * set the canvas width; It's ok to be wider, the x-axis floats underneath depending
     * on the main graph pan/zoom.  We can be as wide as we need to be.
     *
     */

    $(this._canvas).width(maxW + 40);
    this._width         = maxW + 40;
    this._canvas.width  = this._width;

    /* draw the axis line (between the labels and their values */

    if (this._nightMode) {

      context.strokeStyle = this._prefs.axis.night.tickColor;
      context.fillStyle   = this._prefs.axis.night.tickColor;

    } else {

      context.strokeStyle = this._prefs.axis.day.tickColor;
      context.fillStyle   = this._prefs.axis.day.tickColor;
    }

    context.lineWidth     = 1;

    /*
     * we draw the axis to minY; because we may have adjusted how many extra stations we
     * show at the top or bottom.
     *
     */

    context.beginPath();
    context.moveTo(left + maxLW + 4, minY);
    context.lineTo(left + maxLW + 4, y1);
    context.stroke();

    /*
     * walk the decision points (from bi to ei) and draw them. for each one we
     * might be drawing its label, its tick mark and/or its mileage value.
     * The vertical position for each was pre-calculated in pre-scan loop above.
     *
     */

    for (let i = ei; i >= bi; i--) {

      const pos = labels.get(i);

      /* draw the label */

      if (this._nightMode) {

        context.strokeStyle = this._prefs.axis.night.fontColor;
        context.fillStyle   = this._prefs.axis.night.fontColor;

      } else {

        context.strokeStyle = this._prefs.axis.day.fontColor;
        context.fillStyle   = this._prefs.axis.day.fontColor;
      }

      /* are we showing station names */

      if (this._showStation) {

        context.textBaseline = 'middle';
        context.textAlign    = 'right';
        context.font         = 'normal bold ' + fs + 'px Verdana';
        context.fillText(pos.text, left + maxLW, pos.y);
      }

      /* draw the tick mark */

      if (this._nightMode) {

        context.strokeStyle = this._prefs.axis.night.tickColor;
        context.fillStyle   = this._prefs.axis.night.tickColor;

      } else {

        context.strokeStyle = this._prefs.axis.day.tickColor;
        context.fillStyle   = this._prefs.axis.day.tickColor;
      }

      context.lineWidth     = 1;

      context.beginPath();
      context.moveTo(left + maxLW + 4, pos.y);
      context.lineTo(left + maxLW + 4 + 12, pos.y);
      context.stroke();

      /* draw the value */

      if (this._nightMode) {

        context.strokeStyle = this._prefs.axis.night.fontColor;
        context.fillStyle   = this._prefs.axis.night.fontColor;

      } else {

        context.strokeStyle = this._prefs.axis.day.fontColor;
        context.fillStyle   = this._prefs.axis.day.fontColor;
      }

      context.textBaseline  = 'middle';
      context.textAlign     = 'left';
      context.font          = 'normal bold ' + fs + 'px Verdana';
      context.fillText(pos.value, left + maxLW + 4 + 12 + 4, pos.y);

    }

    /* draw the minor ticks (every 5 miles) */

  }

  /** @brief handle resizing of parent (and then our bounds) */
  resize(): void {

    this._width  = $(this._canvas).width();
    this._height = $(this._canvas).height();

    this._canvas.width  = this._width;
    this._canvas.height = this._height;

    // Logger.debug('[distance]', 'resized, w:', this._width, ' h:', this._height);

    this.update();
  }

  /** @brief set the display mode (dark vs light) */
  setNightMode(flag: boolean) {

    if (flag) {

      $(this._canvas).removeClass('day');
      $(this._canvas).addClass('night');

    } else {

      $(this._canvas).removeClass('night');
      $(this._canvas).addClass('day');
    }

    this._nightMode = flag;

  }

}
